import { Either } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

const JSON_TYPE_REQUEST = 'application/json';

/**
 * @description Категория товара.
 * @param id
 * @param name
 * @constructor
 */
export function ProductCategory(
  id = null,
  name = '',
) {
  this.id = id;
  this.name = name;
}

/**
 * @description Единица измерения товара.
 * @param {string|null} id
 * @param {string}      name
 * @param {boolean}     isPack
 * @param {string}      shortName
 * @param {string}      note
 * @constructor
 */
export function ProductUnit(
  id = null,
  name = '',
  isPack = false,
  shortName = '',
  note = '',
) {
  this.id = id;
  this.name = name;
  this.isPack = isPack;
  this.shortName = shortName;
  this.note = note;
}

/**
 * @description Запрос категории товара.
 * @param {string|null} id
 * @param {string}      name
 * @constructor
 */
export function ProductCategoryRequest(
  id = null,
  name = '',
) {
  this.getId = () => id;
  this.toQuery = () => ({
    name,
  });
}

/**
 * @description Запрос единицы измерения товара.
 * @param {string|null} id
 * @param {string}      name
 * @param {string}      shortName
 * @param {string}      note
 * @constructor
 */
export function ProductUnitRequest(
  id = null,
  name = '',
  shortName = '',
  note = '',
) {
  this.getId = () => id;
  this.toQuery = () => ({
    name,
    short_name: shortName,
    note,
  });
}

/**
 * @description Запрос товара.
 * @param {string|null} id
 * @param {string} name
 * @param {string} manufacturer
 * @param {string} category
 * @param {string} unit
 * @param {string} vendor
 * @param {string} countInPack
 * @constructor
 */
export function ProductRequest(
  id = null,
  name = '',
  manufacturer = '',
  category = '',
  unit = '',
  vendor = '',
  countInPack = '',
  criticalStockLevel = 0,
) {
  this.getId = () => id;
  this.getCategoryId = () => category;
  this.toQuery = () => ({
    name,
    manufacturer,
    category_id: category,
    unit_id: unit,
    count_in_pack: countInPack,
    vendor,
    critical_stock_level: criticalStockLevel,
  });
}

/**
 * @description Поставщик.
 * @param {string|null} id
 * @param {string}      name
 * @param {string}      manufacturer
 * @param {string}      category
 * @param {string}      unit
 * @param {string}      vendor
 * @param {number}      countInPack
 * @param {number}      criticalStockLevel
 * @param {number}      balance
 * @param {number}      index
 * @constructor
 */
export function Product(
  id = null,
  name = '',
  manufacturer = '',
  category = '',
  unit = '',
  vendor = '',
  countInPack = 0,
  criticalStockLevel = 0,
  balance = 0,
  index = 0,
) {
  this.id = id;
  this.name = name;
  this.manufacturer = manufacturer;
  this.category = category;
  this.unit = unit;
  this.vendor = vendor;
  this.countInPack = countInPack;
  this.criticalStockLevel = criticalStockLevel;
  this.balance = balance;
  this.index = index;
}

/**
 * @description Шлюз Товаров.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function ProductGateway(axios) {
  /**
   * @description Просмотр товара.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.viewProduct = (id) => axios
    .get(`/api/warehouse/products/${id}`)
    .then(
      async (response) => Either.right({
        product: new Product(
          response.data.id,
          response.data.name,
          response.data.manufacturer,
          response.data.category_id,
          response.data.unit_id,
          response.data.vendor_id,
          response.data.count_in_pack,
          response.data.critical_stock_level,
        ),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Просмотр единицы измерения товара.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.viewProductUnit = (id) => axios
    .get(`/api/warehouse/product/units/${id}`)
    .then(
      async (response) => Either.right({
        productUnit: new ProductUnit(
          response.data.id,
          response.data.name,
          response.data.is_pack,
          response.data.short_name,
          response.data.note,
        ),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Создание товара.
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.create = (params) => axios
    .post('/api/warehouse/products', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Создание категории товара.
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.createProductCategory = (params) => axios
    .post('/api/warehouse/product/categories', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Создание единицы измерения товара.
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.createProductUnit = (params) => axios
    .post('/api/warehouse/product/units', params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удаление товара.
   * @param {string} id Идентификатор товара.
   * @returns {Promise<Either>}
   */
  this.delete = (id) => axios
    .delete(`/api/warehouse/products/${id}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удаление категории товара.
   * @param {string} id Идентификатор категории товара.
   * @returns {Promise<Either>}
   */
  this.deleteProductCategory = (id) => axios
    .delete(`/api/warehouse/product/categories/${id}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удаление единицы измерения товара.
   * @param {string} id
   * @returns {Promise<Either>}
   */
  this.deleteProductUnit = (id) => axios
    .delete(`/api/warehouse/product/units/${id}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Редактирование товара.
   * @param {string} id
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.edit = (id, params) => axios
    .put(`/api/warehouse/products/${id}`, params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Редактирование категории товара.
   * @param {string} id
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.editProductCategory = (id, params) => axios
    .put(`/api/warehouse/product/categories/${id}`, params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Редактирование единицы измерения товара.
   * @param {string} id
   * @param {Object} params
   * @returns {Promise<Either>}
   */
  this.editProductUnit = (id, params) => axios
    .put(`/api/warehouse/product/units/${id}`, params)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Получить всех Товаров.
   * @param {{type:String, order:String}} sort        Параметры сортировки
   * @param {number}                      page        Номер страницы.
   * @param {array}                       categories  Категории.
   * @param {string}                      searchQuery Текст поиска.
   * @returns {Promise<Either>}
   */
  this.getAll = (
    sort = { type: '', order: '' },
    page = 1,
    categories = [],
    searchQuery = '',
  ) => {
    const params = {
      sort_type: sort.type,
      sort_order: sort.order,
      page,
      categories,
      search: searchQuery,
    };
    return axios
      .get(
        '/api/warehouse/products',
        {
          headers: { accept: JSON_TYPE_REQUEST },
          params,
        },
      ).then(
        async (response) => {
          const { data } = response;
          const pageCount = data.page_count;
          if (Array.isArray(data.products)) {
            const products = data.products.map((item, key) => (new Product(
              item.id,
              item.name,
              item.manufacturer,
              item.category,
              item.unit,
              '',
              0,
              item.critical_stock_level,
              item.balance,
              parseInt(key, 10),
            )));
            return Either.right({
              actions: data.actions || [],
              products,
              pageCount,
              categories: data.categories,
            });
          }
          const keys = Object.keys(data.products);
          const products = [];
          keys.forEach(
            (key) => {
              const item = data.products[key];
              products.push(new Product(
                item.id,
                item.name,
                item.manufacturer,
                item.category,
                item.unit,
                '',
                0,
                item.balance,
                parseInt(key, 10),
              ));
            },
          );
          return Either.right({
            actions: data.actions || [],
            products,
            pageCount,
            categories: data.categories,
          });
        },
        async (error) => {
          if (error.isAxiosError && [400, 403].includes(error.response.status)) {
            const { causes, title } = error.response.data;
            const leftResponse = new LeftResponse(causes, title);
            return Either.left(leftResponse);
          }
          throw error;
        },
      );
  };
  /**
   * @description Получить все единицы измерения.
   * @param {{type:String, order:String}} sort        Параметры сортировки
   * @param {number}                      page        Номер страницы.
   * @returns {Promise<Either>}
   */
  this.getProductUnitList = (
    sort = { type: '', order: '' },
    page = 1,
  ) => axios
    .get(
      '/api/warehouse/product/units',
      {
        headers: { accept: JSON_TYPE_REQUEST },
        params: {
          sort_type: sort.type,
          sort_order: sort.order,
          page,
        },
      },
    ).then(
      async (response) => {
        const { data } = response;
        const pageCount = data.page_count;
        const productUnits = data.units.map((item) => (new ProductUnit(
          item.id,
          item.name,
          false,
          item.short_name,
        )));
        return Either.right({
          productUnits,
          pageCount,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Показать опции.
   * @returns {Promise<Either>}
   */
  this.viewOptions = () => axios
    .get('/api/warehouse/product/options')
    .then(
      async (response) => {
        const categories = response.data.categories.map((item) => (new ProductCategory(
          item.id,
          item.name,
        )));
        const units = response.data.units.map((item) => (new ProductUnit(
          item.id,
          item.name,
          item.is_pack,
        )));
        const vendors = response.data.vendors.map((item) => ({
          id: item.id,
          name: item.name,
        }));
        return Either.right({
          categories,
          units,
          vendors,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
